// API 基础地址
const API_BASE = process.env.REACT_APP_API_BASE !== undefined
  ? process.env.REACT_APP_API_BASE
  : (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// 门店管理端 API 前缀
export const API_PREFIX = `${API_BASE}/store-mgr`;

// 分页默认每页数
export const PAGE_SIZE = 20;

// 商品状态
export const PRODUCT_STATUS = {
  OFF_SHELF: 0,
  ON_SHELF: 1,
} as const;

export const PRODUCT_STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '已下架', color: 'default' },
  1: { text: '已上架', color: 'success' },
};

// 商品类型
export const PRODUCT_TYPE_MAP: Record<string, string> = {
  PHYSICAL: '实物商品',
  VIRTUAL: '虚拟商品',
};

// 供应商结算方式
export const SETTLE_TYPE_MAP: Record<string, string> = {
  alipay: '支付宝',
  bank: '银行卡',
};

// 导入模板类型
export const IMPORT_TEMPLATE_TYPE = {
  SINGLE: 'single',
  MULTI: 'multi',
} as const;

// 导入状态
export const IMPORT_STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '校验中', color: 'processing' },
  1: { text: '校验失败', color: 'error' },
  2: { text: '待确认', color: 'warning' },
  3: { text: '导入成功', color: 'success' },
  4: { text: '已取消', color: 'default' },
  5: { text: '导入中', color: 'processing' },
};

// 价格：分 -> 元
export const fen2yuan = (fen: number): string => (fen / 100).toFixed(2);

// 价格：元 -> 分
export const yuan2fen = (yuan: number): number => Math.round(yuan * 100);

// ==================== 服务管理相关常量 ====================

// 服务类型
export const SERVICE_TYPE_MAP: Record<number, string> = {
  1: '洗护',
  2: '寄养',
  3: '医疗',
};

export const SERVICE_TYPE_OPTIONS = [
  { label: '洗护', value: 1 },
  { label: '寄养', value: 2 },
  { label: '医疗', value: 3 },
];

// 服务状态
export const SERVICE_STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '下架', color: 'default' },
  1: { text: '上架', color: 'success' },
};

// 星期映射
export const DAY_OF_WEEK_MAP: Record<number, string> = {
  1: '周一',
  2: '周二',
  3: '周三',
  4: '周四',
  5: '周五',
  6: '周六',
  7: '周日',
};

export const DAY_OF_WEEK_OPTIONS = [
  { label: '周一', value: 1 },
  { label: '周二', value: 2 },
  { label: '周三', value: 3 },
  { label: '周四', value: 4 },
  { label: '周五', value: 5 },
  { label: '周六', value: 6 },
  { label: '周日', value: 7 },
];

// ==================== 品种管理 ====================

// 品种类型（固定4个）
export const PET_CATEGORY_OPTIONS = [
  { label: '猫咪', value: 1 },
  { label: '狗狗', value: 2 },
  { label: '小宠', value: 3 },
  { label: '其它', value: 4 },
];

export const PET_CATEGORY_MAP: Record<number, string> = {
  1: '猫咪',
  2: '狗狗',
  3: '小宠',
  4: '其它',
};

// 员工角色
export const ROLE_CODE_OPTIONS = [
  { label: '店长', value: 'STORE_MANAGER' },
  { label: '店员', value: 'CLERK' },
];

export const ROLE_CODE_MAP: Record<string, string> = {
  SUPER_ADMIN: '超管',
  STORE_MANAGER: '店长',
  CLERK: '店员',
};

export const ROLE_TAG_COLOR: Record<string, string> = {
  SUPER_ADMIN: 'red',
  STORE_MANAGER: 'blue',
  CLERK: 'green',
};

// ==================== 会员管理 ====================

export const MEMBER_LEVELS = [
  { value: 'LEVEL0', label: '普通会员' },
  { value: 'LEVEL1', label: 'VIP会员' },
  { value: 'LEVEL2', label: 'SVIP会员' },
];

export const MEMBER_LEVEL_COLOR: Record<string, string> = {
  LEVEL0: 'default',
  LEVEL1: 'gold',
  LEVEL2: 'purple',
};

// 日期格式化
export const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// 金额格式化（分 -> 元）
export const formatPrice = (price: number | undefined): string => {
  if (price === undefined || price === null) return '-';
  return (price / 100).toFixed(2);
};

// ==================== 会员详情页相关 ====================

// 性别选项
export const GENDER_OPTIONS = [
  { value: 0, label: '未知' },
  { value: 1, label: '男' },
  { value: 2, label: '女' },
];

// 订单状态
export const ORDER_STATUS: Record<number, { text: string; color: string }> = {
  0: { text: '待支付', color: 'default' },
  1: { text: '已支付待处理', color: 'processing' },
  2: { text: '已发货/待提货', color: 'warning' },
  3: { text: '已完成', color: 'success' },
  4: { text: '已取消', color: 'error' },
  5: { text: '已关闭', color: 'error' },
};

// 服务订单状态
export const SERVICE_ORDER_STATUS: Record<number, { text: string; color: string }> = {
  0: { text: '待接单', color: 'default' },
  1: { text: '待上门', color: 'processing' },
  2: { text: '已到店', color: 'warning' },
  3: { text: '已完成', color: 'success' },
  4: { text: '已取消', color: 'error' },
  5: { text: '已逾期', color: 'volcano' },
  6: { text: '服务进行中', color: 'cyan' },
  7: { text: '待上门', color: 'gold' },
  8: { text: '已拒绝', color: 'warning' },
};

// ==================== 医疗订单 ====================

// 医疗业务状态：0待支付 1待平台确认 2已确认/已完成 3已拒绝 4用户已取消 5确认超时 6服务进行中
export const MEDICAL_BUSINESS_STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '待支付', color: 'default' },
  1: { text: '待平台确认', color: 'processing' },
  2: { text: '已确认/已完成', color: 'success' },
  3: { text: '已拒绝', color: 'error' },
  4: { text: '用户已取消', color: 'warning' },
  5: { text: '确认超时', color: 'volcano' },
  6: { text: '服务进行中', color: 'cyan' },
};

// 医疗支付状态：0待支付 1已支付 2退款中 3已退款 4已关闭
export const MEDICAL_PAY_STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '待支付', color: 'warning' },
  1: { text: '已支付', color: 'success' },
  2: { text: '退款中', color: 'processing' },
  3: { text: '已退款', color: 'default' },
  4: { text: '已关闭', color: 'error' },
};

// 医疗退款状态
export const MEDICAL_REFUND_STATUS_MAP: Record<number, string> = {
  0: '无退款',
  1: '退款中',
  2: '退款成功',
  4: '退款失败',
};

// 医疗付款类型
export const MEDICAL_PAYMENT_TYPE_MAP: Record<string, string> = {
  MALE_STERILIZATION: '公猫绝育',
  FEMALE_STERILIZATION: '母猫绝育',
  TREATMENT: '治疗付费',
};

// 医疗支付方式
export const MEDICAL_PAY_TYPE_MAP: Record<string, string> = {
  WECHAT: '微信支付',
  BALANCE: '余额支付',
  COUPON: '优惠券抵扣',
};

// 医疗日志操作人类型
export const MEDICAL_OPERATOR_TYPE_MAP: Record<string, string> = {
  USER: '用户',
  ADMIN: '平台',
  SYSTEM: '系统',
  WECHAT: '微信支付',
};

// 宠物性别
export const PET_GENDER: Record<number, string> = {
  0: '未知',
  1: '公',
  2: '母',
};

// 绝育状态
export const STERILIZED_STATUS: Record<number, string> = {
  0: '未知',
  1: '已绝育',
  2: '未绝育',
};

// ==================== 活体管理 ====================

// 活体状态选项（已死亡仅在编辑已有活体时展示）
export const LIVE_PET_STATUS_OPTIONS = [
  { label: '未出售', value: 1 },
  { label: '已预订', value: 2 },
  { label: '已出售', value: 3 },
  { label: '已赠送', value: 4 },
  { label: '已死亡', value: 5 },
];

// 活体状态筛选选项（仅用于列表筛选，不影响表单录入与列表展示）
export const LIVE_PET_STATUS_FILTER_OPTIONS = [
  { label: '未出售', value: 1 },
  { label: '已预订', value: 2 },
  { label: '已出售', value: 3 },
  { label: '已赠送', value: 4 },
  { label: '已死亡', value: 5 },
];

// 活体状态映射
export const LIVE_PET_STATUS_MAP: Record<number, { text: string; color: string }> = {
  1: { text: '未出售', color: 'blue' },
  2: { text: '已预订', color: 'orange' },
  3: { text: '已出售', color: 'green' },
  4: { text: '已赠送', color: 'purple' },
  5: { text: '已死亡', color: 'red' },
  6: { text: '已调拨', color: 'cyan' },
  7: { text: '待领养', color: 'gold' },
  8: { text: '已领养', color: 'lime' },
};

// 活体性别选项
export const LIVE_PET_GENDER_OPTIONS = [
  { label: '弟弟', value: 1 },
  { label: '妹妹', value: 2 },
];

// 活体性别映射
export const LIVE_PET_GENDER_MAP: Record<number, string> = {
  1: '弟弟',
  2: '妹妹',
};

// 驱虫类型
export const DEWORMING_TYPE_OPTIONS = [
  { label: '内驱', value: 1 },
  { label: '外驱', value: 2 },
];

export const DEWORMING_TYPE_MAP: Record<number, string> = {
  1: '内驱',
  2: '外驱',
};

// ==================== 收银台 ====================

// 支付方式
export const CASHIER_PAY_METHOD = {
  WECHAT: 'wechat',
  ALIPAY: 'alipay',
  BALANCE: 'balance',
  MEMBER_BALANCE: 'member_balance',
} as const;

export const CASHIER_PAY_METHOD_MAP: Record<string, string> = {
  wechat: '微信支付',
  alipay: '支付宝',
  balance: '余额支付',
  member_balance: '会员支付',
};

// 顾客类型
export const CUSTOMER_TYPE = {
  MEMBER: 1,
  GUEST: 2,
} as const;

export const CUSTOMER_TYPE_MAP: Record<number, string> = {
  1: '会员',
  2: '散客',
};
